import { useState, useCallback, useEffect } from "react"
import { TEAMS } from "./nodes/teams"

function normalizeStoriesInput(text) {
  return (text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

const TEAM_OPTIONS = Object.keys(TEAMS)

const TEAM_NAMES = {
  payments: "Payments",
  checkout: "Checkout",
  billing: "Billing",
  growth: "Growth",
  platform: "Platform",
  core: "Core",
}

function SourceTab({ label, active, onClick }) {
  return (
    <button
      type="button"
      className={`source-tab ${active ? "active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function SourcePanel({ node, type, flowSlug, onUpdate }) {
  const sources = node?.data?.sources?.[type]
  const glossaryTerms = node?.data?.glossaryTerms || []

  const matchedGlossaryTerms = (() => {
    if (!glossaryTerms.length) return []
    const label = (node?.data?.label || "").toString().toLowerCase()
    if (!label) return []

    const matches = []
    for (const t of glossaryTerms) {
      const needles = [t.term, ...(t.aliases || [])].filter(Boolean)
      if (needles.some((n) => label.includes(n.toLowerCase()))) {
        matches.push(t)
      }
    }
    // stable ordering: high risk first, then alpha
    return matches.sort((a, b) => {
      if (b.confusion_risk !== a.confusion_risk) return b.confusion_risk - a.confusion_risk
      return a.term.localeCompare(b.term)
    })
  })()
  const [isEditingDesired, setIsEditingDesired] = useState(false)
  const [intentDraft, setIntentDraft] = useState("")
  const [storiesDraft, setStoriesDraft] = useState("")
  const [desiredSaving, setDesiredSaving] = useState(false)
  const [desiredError, setDesiredError] = useState(null)
  const [savedTick, setSavedTick] = useState(false)

  useEffect(() => {
    if (type !== "desired") return

    const currentIntent = sources?.intent || ""
    const currentStories = (sources?.stories || []).join("\n")

    setIntentDraft(currentIntent)
    setStoriesDraft(currentStories)
    setIsEditingDesired(false)
    setDesiredError(null)
    setSavedTick(false)
  }, [type, node?.id])

  const saveDesired = useCallback(async () => {
    if (!node?.id) return

    setDesiredSaving(true)
    setDesiredError(null)
    setSavedTick(false)

    try {
      const response = await fetch(`/flows/${flowSlug}/nodes/${node.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify({
            node: {
              sources: {
                desired: {
                  intent: intentDraft,
                  stories: normalizeStoriesInput(storiesDraft),
                },
              },
            },
          }),
        }
      )

      if (!response.ok) throw new Error("Failed to save desired sources")

      const data = await response.json()
      onUpdate?.(node.id, data.node)
      setIsEditingDesired(false)
      setSavedTick(true)
      setTimeout(() => setSavedTick(false), 1200)
    } catch (err) {
      setDesiredError(err.message)
    } finally {
      setDesiredSaving(false)
    }
  }, [flowSlug, intentDraft, storiesDraft, node?.id, onUpdate])

  if (!sources) {
    return <p className="empty-source">No {type} sources linked</p>
  }

  switch (type) {
    case "expected":
      return (
        <div className="source-content">
          {sources.tests?.length > 0 && (
            <div className="source-section">
              <h4>Tests</h4>
              <ul>
                {sources.tests.map((test, i) => {
                  // Extract case number (e.g., "C1001" -> "1001")
                  const caseNumber = test.toString().replace(/^C/i, "")
                  const testRailUrl = `https://elopage.testrail.io/index.php?/cases/view/${caseNumber}`

                  return (
                    <li key={i}>
                      <a
                        href={testRailUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#4f46e5",
                          textDecoration: "underline",
                          fontFamily: "monospace",
                          fontSize: "12px",
                        }}
                      >
                        {test}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {sources.gaps?.length > 0 && (
            <div className="source-section">
              <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Gaps
                <span
                  style={{
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#f59e0b",
                    color: "white",
                    fontWeight: 700,
                  }}
                >
                  {sources.gaps.length}
                </span>
              </h4>
              <ul>
                {sources.gaps.map((gap, i) => (
                  <li key={i} style={{ color: "#b45309" }}>{gap}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )

    case "actual":
      return (
        <div className="source-content">
          {sources.codeRefs?.length > 0 && (
            <div className="source-section">
              <h4>Code References</h4>
              <ul>
                {sources.codeRefs.map((ref, i) => (
                  <li key={i}>
                    <code style={{ wordBreak: "break-all", overflowWrap: "anywhere", display: "inline-block", maxWidth: "100%" }}>{ref}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )

    case "desired":
      return (
        <div className="source-content">
          <div className="source-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <h4 style={{ margin: 0 }}>Desired</h4>
              {!isEditingDesired ? (
                <button
                  type="button"
                  className="edit-btn"
                  onClick={() => setIsEditingDesired(true)}
                  title="Edit intent and stories"
                >
                  Edit Intent
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="save-btn"
                    onClick={saveDesired}
                    disabled={desiredSaving}
                  >
                    {desiredSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setIsEditingDesired(false)}
                    disabled={desiredSaving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {desiredError && <div className="error-message" style={{ marginTop: 8 }}>{desiredError}</div>}
            {savedTick && <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a" }}>Saved</div>}

            {isEditingDesired ? (
              <div style={{ marginTop: 10 }}>
                <div className="form-group">
                  <label htmlFor="desired-intent">Intent</label>
                  <textarea
                    id="desired-intent"
                    className="edit-input"
                    value={intentDraft}
                    onChange={(e) => setIntentDraft(e.target.value)}
                    placeholder="What is the desired behavior/outcome?"
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="desired-stories">User Stories (one per line)</label>
                  <textarea
                    id="desired-stories"
                    className="edit-input"
                    value={storiesDraft}
                    onChange={(e) => setStoriesDraft(e.target.value)}
                    placeholder="As a ...\nI want ...\nSo that ..."
                    rows={6}
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                {sources.intent ? (
                  <div className="source-section">
                    <h4>Intent</h4>
                    <p>{sources.intent}</p>
                  </div>
                ) : (
                  <p className="empty-source">No intent captured yet.</p>
                )}

                {sources.stories?.length > 0 ? (
                  <div className="source-section">
                    <h4>Stories</h4>
                    <ul>
                      {sources.stories.map((story, i) => (
                        <li key={i}>{story}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="empty-source">No user stories captured yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )

    default:
      return null
  }
}

export default function NodeEditPanel({
  node,
  flowSlug,
  onUpdate,
  onDelete,
  onClose,
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState("expected")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    label: node?.data?.label || "",
    internalLabel: node?.data?.internalLabel || "",
    description: node?.data?.description || "",
    team: node?.data?.team || "",
    tags: node?.data?.tags?.join(", ") || "",
    drillDownSlug: node?.data?.drillDownSlug || "",
  })

  // Update formData when node changes
  useEffect(() => {
    if (node) {
      setFormData({
        label: node.data?.label || "",
        internalLabel: node.data?.internalLabel || "",
        description: node.data?.description || "",
        team: node.data?.team || "",
        tags: node.data?.tags?.join(", ") || "",
        drillDownSlug: node.data?.drillDownSlug || "",
      })
      setIsEditing(false)
      setError(null)
      setConfirmDelete(false)
    }
  }, [node])

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)

    try {
      const tagsArray = formData.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t)

      const response = await fetch(`/flows/${flowSlug}/nodes/${node.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          node: {
            label: formData.label,
            internalLabel: formData.internalLabel,
            description: formData.description,
            team: formData.team || null,
            tags: tagsArray,
            drillDownSlug: formData.drillDownSlug || null,
          },
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to save")
      }

      const data = await response.json()
      onUpdate?.(node.id, data.node)
      setIsEditing(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }, [formData, flowSlug, node?.id, onUpdate])

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }

    setSaving(true)
    setError(null)

    try {
      const response = await fetch(`/flows/${flowSlug}/nodes/${node.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to delete")
      }

      onDelete?.(node.id)
      onClose?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
      setConfirmDelete(false)
    }
  }, [confirmDelete, flowSlug, node?.id, onDelete, onClose])

  const handleCancel = useCallback(() => {
    setFormData({
      label: node?.data?.label || "",
      internalLabel: node?.data?.internalLabel || "",
      description: node?.data?.description || "",
      team: node?.data?.team || "",
      tags: node?.data?.tags?.join(", ") || "",
      drillDownSlug: node?.data?.drillDownSlug || "",
    })
    setIsEditing(false)
    setError(null)
    setConfirmDelete(false)
  }, [node])

  if (!node) {
    return (
      <div className="node-edit-panel empty">
        <div className="empty-state">
          <div className="empty-icon">◎</div>
          <p>Select a node to view details</p>
        </div>
      </div>
    )
  }

  const nodeType = node.type || "process"
  const hasDetails = node.data.description || node.data.team || node.data.tags?.length > 0

  return (
    <div className="node-edit-panel has-node">
      <div className="panel-header">
        <div className="header-top">
          <span className={`node-type-badge ${nodeType}`}>{nodeType}</span>
          <div className="header-actions">
            {!isEditing && (
              <button
                type="button"
                className="edit-btn"
                onClick={() => setIsEditing(true)}
                title="Edit node"
              >
                ✎
              </button>
            )}
            <button type="button" className="close-btn" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="header-content">
          {isEditing ? (
            <input
              type="text"
              className="edit-input title-input"
              value={formData.label}
              onChange={(e) => handleInputChange("label", e.target.value)}
              placeholder="Node label"
              autoFocus
            />
          ) : (
            <h3 className="node-title">{node.data.label}</h3>
          )}

          {!isEditing && node.data.internalLabel && (
            <span className="internal-label">{node.data.internalLabel}</span>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isEditing ? (
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="internalLabel">Internal Label</label>
            <input
              type="text"
              id="internalLabel"
              className="edit-input"
              value={formData.internalLabel}
              onChange={(e) => handleInputChange("internalLabel", e.target.value)}
              placeholder="Internal identifier"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="edit-input"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Node description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="team">Team</label>
            <select
              id="team"
              className="edit-input"
              value={formData.team}
              onChange={(e) => handleInputChange("team", e.target.value)}
            >
              <option value="">No team</option>
              {TEAM_OPTIONS.map((team) => (
                <option key={team} value={team}>
                  {TEAM_NAMES[team] || team}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              type="text"
              id="tags"
              className="edit-input"
              value={formData.tags}
              onChange={(e) => handleInputChange("tags", e.target.value)}
              placeholder="Comma-separated tags"
            />
          </div>

          <div className="form-group">
            <label htmlFor="drillDownSlug">Drill-down Flow</label>
            <input
              type="text"
              id="drillDownSlug"
              className="edit-input"
              value={formData.drillDownSlug}
              onChange={(e) => handleInputChange("drillDownSlug", e.target.value)}
              placeholder="Flow slug for drill-down"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>

          <div className="danger-zone">
            <button
              type="button"
              className={`delete-btn ${confirmDelete ? "confirm" : ""}`}
              onClick={handleDelete}
              disabled={saving}
            >
              {confirmDelete ? "Confirm Delete" : "Delete Node"}
            </button>
            {confirmDelete && (
              <button
                type="button"
                className="cancel-delete-btn"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="view-content">
          {/* Node Details Section */}
          <div className="details-section">
            {node.data.description && (
              <p className="node-description">{node.data.description}</p>
            )}

            {/* Code References (from actual sources) */}
            {node.data?.sources?.actual?.codeRefs?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Code References
                </div>
                <ul style={{ marginTop: 6, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                  {node.data.sources.actual.codeRefs.map((ref, i) => (
                    <li key={i}>
                      <code style={{ fontSize: 12, fontFamily: "monospace", color: "#334155", background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, wordBreak: "break-all", overflowWrap: "anywhere", display: "inline-block", maxWidth: "100%" }}>{ref}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Domain Lens glossary matches */}
            {node.data?.glossaryTerms?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Glossary matches
                </div>
                {(() => {
                  const label = (node.data?.label || "").toString().toLowerCase()
                  const terms = (node.data?.glossaryTerms || [])
                    .filter((t) => {
                      const needles = [t.term, ...(t.aliases || [])].filter(Boolean)
                      return needles.some((n) => label.includes(n.toLowerCase()))
                    })
                    .sort((a, b) => b.confusion_risk - a.confusion_risk)

                  if (terms.length === 0) return <div className="empty-source" style={{ padding: 0, marginTop: 6 }}>None</div>

                  return (
                    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
                      {terms.map((t) => {
                        const riskLabel = t.confusion_risk === 2 ? "High" : t.confusion_risk === 1 ? "Medium" : "Low"
                        const badgeBg = t.confusion_risk === 2 ? "#fee2e2" : t.confusion_risk === 1 ? "#fef3c7" : "#d1fae5"
                        const badgeText = t.confusion_risk === 2 ? "#991b1b" : t.confusion_risk === 1 ? "#92400e" : "#065f46"

                        return (
                          <div key={t.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#fafafa" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                              <a href={`/domain_terms/${t.id}`} style={{ fontWeight: 700, color: "#3730a3", textDecoration: "underline" }}>
                                {t.term}
                              </a>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: badgeBg, color: badgeText }}>
                                {riskLabel}
                              </span>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12, color: "#334155", whiteSpace: "pre-wrap" }}>
                              {t.definition || "No definition."}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            )}
            {node.data.team && (
              <div className="node-team">
                <span
                  className="team-dot"
                  style={{ backgroundColor: TEAMS[node.data.team] }}
                />
                {TEAM_NAMES[node.data.team] || node.data.team}
              </div>
            )}

            {node.data.tags?.length > 0 && (
              <div className="node-tags">
                {node.data.tags.map((tag, i) => (
                  <span key={i} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {node.data.drillDownSlug && (
              <a
                href={`/flows/${node.data.drillDownSlug}`}
                className="drill-down-link"
              >
                <span className="drill-icon">⤵</span>
                View Sub-flow
              </a>
            )}

            {!hasDetails && !node.data.drillDownSlug && (
              <p className="no-details">No additional details. Click edit to add.</p>
            )}
          </div>

          {/* Sources Section */}
          <div className="sources-section">
            <h4 className="section-header">Sources</h4>
            <div className="source-tabs">
              <div className="tab-header">
                <SourceTab
                  label="Expected"
                  active={activeTab === "expected"}
                  onClick={() => setActiveTab("expected")}
                />
                <SourceTab
                  label="Actual"
                  active={activeTab === "actual"}
                  onClick={() => setActiveTab("actual")}
                />
                <SourceTab
                  label="Desired"
                  active={activeTab === "desired"}
                  onClick={() => setActiveTab("desired")}
                />
              </div>
              <div className="tab-content">
                <SourcePanel node={node} type={activeTab} flowSlug={flowSlug} onUpdate={onUpdate} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
